import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { useT } from '../i18n';

// Keys are matched by physical position (event.code), so shortcuts work on
// macOS (where Option+D types a symbol) and with any keyboard layout.
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent || '');
const ALT = IS_MAC ? '⌥' : 'Alt';
const MOD = IS_MAC ? '⌘' : 'Ctrl';

const ROUTES = {
  KeyD: '/dashboard',
  KeyC: '/campaigns',
  KeyA: '/analytics',
  KeyN: '/campaigns?new=1',
  KeyS: '/settings',
  KeyL: '/clicks/log',
};

const SHORTCUTS = [
  { keys: ALT + ' D', label: 'Dashboard' },
  { keys: ALT + ' C', label: 'Campaigns' },
  { keys: ALT + ' A', label: 'Analytics' },
  { keys: ALT + ' N', label: 'New campaign' },
  { keys: ALT + ' S', label: 'Settings' },
  { keys: ALT + ' L', label: 'Clicks log' },
  { keys: MOD + ' K', label: 'Command palette' },
  { keys: '?', label: 'Show shortcuts' },
];

export default function KeyboardShortcuts() {
  const navigate = useNavigate();
  const { t } = useT();
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable) {
        return;
      }

      if (e.altKey && !e.ctrlKey && !e.metaKey) {
        const fallback = 'Key' + (e.key || '').toUpperCase();
        const route = ROUTES[e.code] || ROUTES[fallback];
        if (route) {
          e.preventDefault();
          navigate(route);
        }
        return;
      }

      const question = e.key === '?' || (e.code === 'Slash' && e.shiftKey);
      if (question && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setShowHelp((v) => !v);
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);

  return (
    <Dialog open={showHelp} onOpenChange={setShowHelp}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>{t('Keyboard shortcuts')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-1 mt-2">
          {SHORTCUTS.map((s) => (
            <div key={s.keys} className="flex items-center justify-between py-1.5">
              <span className="text-sm text-muted-foreground">{t(s.label)}</span>
              <kbd className="inline-flex items-center gap-1 rounded-md border border-border bg-surface-2 px-2 py-0.5 text-xs text-muted-foreground">
                {s.keys}
              </kbd>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
